import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import {
  ShieldCheck, FileText, Envelope, Timer, MegaphoneSimple,
  Heartbeat, Trash, ArrowsClockwise, CheckCircle,
  MagnifyingGlass, UserCircle, DownloadSimple, EyeSlash, X,
  FilePdf,
} from "@phosphor-icons/react";

/**
 * v0.97 — Painel de Privacidade (Motor de Privacidade AgentUS).
 *
 * Não exibe consentimento pro cliente final. Todos os controles aqui
 * são pro TENANT (dono) cumprir LGPD invisível pro usuário.
 */

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">
          {label}
        </label>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Card({ icon: Icon, title, description, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
          <Icon size={18} weight="bold" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, label, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex items-center justify-between text-left rounded-xl border px-4 py-3 transition
        ${checked ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}
        ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <span className="text-sm text-slate-800">{label}</span>
      <span className={`inline-flex w-9 h-5 rounded-full items-center transition ${checked ? "bg-slate-900" : "bg-slate-200"}`}>
        <span className={`h-4 w-4 bg-white rounded-full shadow transform transition ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
    </button>
  );
}

export default function PrivacyPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sectorId, setSectorId] = useState("automotive");
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Ações por cliente
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBusy, setCustomerBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 8);
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.phone || "").includes(q)
    ).slice(0, 8);
  }, [customers, customerSearch]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/privacy/settings");
      setSettings(r.data.settings);
      setSectorId(r.data.sector_id || "automotive");
    } catch (e) {
      toast.error("Falha ao carregar configurações.");
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const r = await api.get("/privacy/events?limit=100");
      setEvents(r.data.items || []);
    } catch (e) {
      /* silencioso — event log é auxiliar */
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => { load(); loadEvents(); }, []);

  useEffect(() => {
    // Carrega clientes pra o buscador (lazy — só se abrir a seção)
    api.get("/customers?limit=500")
      .then((r) => setCustomers(r.data?.items || r.data || []))
      .catch(() => setCustomers([]));
  }, []);

  const downloadComplianceReport = async () => {
    setReportBusy(true);
    try {
      const r = await api.get("/privacy/compliance-report", {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `lgpd_conformidade_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Relatório gerado. Guarde em local seguro.");
      loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar relatório.");
    } finally {
      setReportBusy(false);
    }
  };

  const runCustomerAction = async (action, confirmMsg) => {
    if (!selectedCustomer) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setCustomerBusy(true);
    try {
      if (action === "export") {
        const r = await api.get(`/privacy/customer/${selectedCustomer.id}/export`);
        // Download JSON
        const blob = new Blob([JSON.stringify(r.data, null, 2)],
          { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `lgpd_export_${selectedCustomer.id}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Export gerado.");
      } else {
        await api.post(`/privacy/customer/${selectedCustomer.id}/${action}`);
        toast.success("Ação executada.");
        if (action === "delete") setSelectedCustomer(null);
      }
      loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na ação.");
    } finally {
      setCustomerBusy(false);
    }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/privacy/settings", patch);
      setSettings(r.data.settings);
      toast.success("Configuração salva.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const isHealth = sectorId === "clinic" || sectorId === "pharmacy";

  if (loading || !settings) {
    return (
      <>
        <Topbar title="Privacidade" subtitle="Motor de conformidade LGPD" />
        <div className="p-8 text-sm text-slate-500">Carregando…</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title="Privacidade"
        subtitle="Motor de conformidade LGPD invisível pro seu cliente"
      />
      <div className="p-6 md:p-8 max-w-4xl space-y-6" data-testid="privacy-page">

        {/* Relatório de Conformidade LGPD — feature de destaque */}
        <div
          className="rounded-2xl p-6 text-white shadow-lg"
          style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)" }}
          data-testid="privacy-compliance-report-card"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <FilePdf size={22} weight="bold" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Relatório de Conformidade LGPD</h3>
              <p className="text-sm text-slate-300 mt-1 leading-relaxed">
                PDF autocontido pra apresentar em fiscalização da ANPD ou processo
                judicial. Contém política em vigor, bases legais, retenção,
                estatísticas agregadas (sem PII) e trilha auditável dos últimos 200
                eventos LGPD deste tenant. Cada emissão é assinada com hash de integridade.
              </p>
              <button
                onClick={downloadComplianceReport}
                disabled={reportBusy}
                data-testid="privacy-download-compliance-report"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white text-slate-900 text-sm font-medium rounded-lg hover:bg-slate-100 disabled:opacity-60"
              >
                <DownloadSimple size={16} weight="bold" />
                {reportBusy ? "Gerando…" : "Baixar relatório (PDF)"}
              </button>
            </div>
          </div>
        </div>

        {/* Política */}
        <Card
          icon={FileText}
          title="Política de Privacidade"
          description="Link acessível ao cliente final via perfil WhatsApp, site e rodapé."
          testid="privacy-policy-card"
        >
          <Field label="URL da política" hint="Ex: https://sua-empresa.com/privacidade">
            <input
              type="url"
              defaultValue={settings.policy_url}
              onBlur={(e) => e.target.value !== settings.policy_url &&
                save({ policy_url: e.target.value })}
              placeholder="https://…"
              data-testid="privacy-policy-url-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="E-mail do encarregado (DPO)" hint="Contato oficial pra pedidos LGPD">
            <input
              type="email"
              defaultValue={settings.dpo_email}
              onBlur={(e) => e.target.value !== settings.dpo_email &&
                save({ dpo_email: e.target.value })}
              placeholder="dpo@sua-empresa.com"
              data-testid="privacy-dpo-email-input"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </Card>

        {/* Marketing opt-in */}
        <Card
          icon={MegaphoneSimple}
          title="Consentimento de Marketing"
          description="Separa atendimento (base contratual) de campanhas (base consentimento)."
          testid="privacy-marketing-card"
        >
          <Toggle
            checked={settings.marketing_optin_required}
            onChange={(v) => save({ marketing_optin_required: v })}
            label="Só disparar campanhas para clientes com opt-in explícito"
            disabled={saving}
            testid="privacy-marketing-optin-toggle"
          />
          <p className="text-xs text-slate-500">
            Recomendado <strong>ligado</strong>. Cliente sem opt-in continua sendo atendido
            normalmente — só fica de fora de campanhas em massa.
          </p>
        </Card>

        {/* Consentimento clínico — só saúde */}
        {isHealth && (
          <Card
            icon={Heartbeat}
            title="Consentimento de Dados Sensíveis"
            description="Art. 11 LGPD — obrigatório em saúde antes de coletar sintoma, exame ou histórico."
            testid="privacy-clinical-card"
          >
            <Toggle
              checked={settings.clinical_consent_required}
              onChange={(v) => save({ clinical_consent_required: v })}
              label="Pedir consentimento antes de coletar dados de saúde"
              disabled={saving}
              testid="privacy-clinical-toggle"
            />
          </Card>
        )}

        {/* Retenção */}
        <Card
          icon={Timer}
          title="Retenção de dados"
          description="Prazo máximo de guarda por tipo de dado (dias)."
          testid="privacy-retention-card"
        >
          <div className="grid grid-cols-3 gap-3">
            <Field label="Mensagens" hint="dias">
              <input
                type="number"
                defaultValue={settings.retention.messages_days}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (v && v !== settings.retention.messages_days) {
                    save({ retention: { messages_days: v } });
                  }
                }}
                data-testid="privacy-retention-messages"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Agendamentos" hint="dias">
              <input
                type="number"
                defaultValue={settings.retention.appointments_days}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (v && v !== settings.retention.appointments_days) {
                    save({ retention: { appointments_days: v } });
                  }
                }}
                data-testid="privacy-retention-appointments"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Documentos" hint="dias">
              <input
                type="number"
                defaultValue={settings.retention.documents_days}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (v && v !== settings.retention.documents_days) {
                    save({ retention: { documents_days: v } });
                  }
                }}
                data-testid="privacy-retention-documents"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </Field>
          </div>
          {isHealth && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-3">
              ⚠️ Setor de saúde: LGPD + Res. CFM 1.821 exigem retenção mínima de 20 anos
              (7.300 dias) para prontuário. Não reduza sem orientação jurídica.
            </p>
          )}
        </Card>

        {/* Ações por cliente (Direitos do titular Art. 18) */}
        <Card
          icon={UserCircle}
          title="Ações por cliente (LGPD)"
          description="Direito ao esquecimento, portabilidade e anonimização — Art. 18 LGPD."
          testid="privacy-customer-actions-card"
        >
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Buscar cliente por nome ou telefone…"
              data-testid="privacy-customer-search"
              className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>

          {!selectedCustomer && filteredCustomers.length > 0 && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {filteredCustomers.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedCustomer(c); setCustomerSearch(""); }}
                  data-testid={`privacy-customer-pick-${c.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm text-slate-900">{c.name || "(sem nome)"}</div>
                    <div className="text-xs text-slate-500">{c.phone}</div>
                  </div>
                  {c.marketing_optin && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700">
                      opt-in ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {!selectedCustomer && !customerSearch && customers.length === 0 && (
            <p className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg" data-testid="privacy-customers-empty">
              Nenhum cliente cadastrado ainda. Quando aparecerem clientes na aba
              &quot;Clientes&quot;, eles ficam disponíveis aqui para exercício de direitos LGPD.
            </p>
          )}

          {selectedCustomer && (
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3" data-testid="privacy-customer-selected">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {selectedCustomer.name}
                  </div>
                  <div className="text-xs text-slate-500">{selectedCustomer.phone}</div>
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-slate-400 hover:text-slate-700"
                  data-testid="privacy-customer-clear"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => runCustomerAction("marketing-optin")}
                  disabled={customerBusy}
                  data-testid="privacy-action-optin"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  <MegaphoneSimple size={14} /> Marketing opt-in
                </button>
                <button
                  onClick={() => runCustomerAction("marketing-optout")}
                  disabled={customerBusy}
                  data-testid="privacy-action-optout"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  <MegaphoneSimple size={14} /> Marketing opt-out
                </button>
                {isHealth && (
                  <button
                    onClick={() => runCustomerAction("clinical-consent")}
                    disabled={customerBusy}
                    data-testid="privacy-action-clinical"
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50 col-span-2"
                  >
                    <Heartbeat size={14} /> Consentimento clínico
                  </button>
                )}
                <button
                  onClick={() => runCustomerAction("export")}
                  disabled={customerBusy}
                  data-testid="privacy-action-export"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  <DownloadSimple size={14} /> Exportar dados (JSON)
                </button>
                <button
                  onClick={() => runCustomerAction("anonymize",
                    "Anonimizar irá remover nome/telefone/documento/email mas manter histórico agregado. Confirma?")}
                  disabled={customerBusy}
                  data-testid="privacy-action-anonymize"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-sm rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  <EyeSlash size={14} /> Anonimizar
                </button>
                <button
                  onClick={() => runCustomerAction("delete",
                    "APAGAR o cliente é IRREVERSÍVEL. O nome vira '[EXCLUÍDO LGPD]' e todos os dados pessoais são apagados. Confirma?")}
                  disabled={customerBusy}
                  data-testid="privacy-action-delete"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-rose-200 text-rose-700 text-sm rounded-lg hover:bg-rose-50 disabled:opacity-50 col-span-2"
                >
                  <Trash size={14} /> Direito ao esquecimento (apagar)
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* Log de eventos LGPD */}
        <Card
          icon={ShieldCheck}
          title="Registro de eventos LGPD"
          description="Trilha auditável — última centena de eventos do tenant."
          testid="privacy-events-card"
        >
          <div className="flex justify-end -mt-2">
            <button
              onClick={loadEvents}
              disabled={eventsLoading}
              data-testid="privacy-events-refresh"
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              <ArrowsClockwise size={12} /> Atualizar
            </button>
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">
              Nenhum evento registrado ainda.
            </p>
          ) : (
            <div className="max-h-96 overflow-y-auto -mx-2">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="px-2 py-2 font-medium">Quando</th>
                    <th className="px-2 py-2 font-medium">Evento</th>
                    <th className="px-2 py-2 font-medium">Base legal</th>
                    <th className="px-2 py-2 font-medium">Ator</th>
                    <th className="px-2 py-2 font-medium">Cliente</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-b border-slate-100">
                      <td className="px-2 py-2 text-slate-500 whitespace-nowrap">
                        {new Date(ev.created_at).toLocaleString("pt-BR", {
                          day: "2-digit", month: "2-digit",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </td>
                      <td className="px-2 py-2 text-slate-900">{ev.event}</td>
                      <td className="px-2 py-2 text-slate-600">{ev.legal_basis}</td>
                      <td className="px-2 py-2 text-slate-600">{ev.actor}</td>
                      <td className="px-2 py-2 text-slate-500 font-mono text-[10px]">
                        {ev.customer_id ? ev.customer_id.slice(0, 10) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="text-xs text-slate-400 pt-4 border-t border-slate-100">
          <p>
            <CheckCircle size={12} className="inline mr-1" />
            Conformidade LGPD invisível pro cliente final. Base legal aplicada
            automaticamente por contexto (atendimento vs marketing vs dados sensíveis).
          </p>
        </div>
      </div>
    </>
  );
}
